import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const MediaKit = ({ prompt, data }) => {
  const { FiDownload, FiBarChart3, FiUsers, FiTrendingUp, FiInstagram, FiTwitter, FiMapPin, FiCamera, FiHeart } = FiIcons;

  const downloadMediaKit = () => {
    alert('Media kit PDF download - would generate professional media kit');
  };

  // Enhanced metrics based on the prompt
  const metrics = {
    followers: '125.4K',
    engagement: '8.2%',
    reach: '2.1M',
    impressions: '5.8M'
  };

  // Provide fallback data if none exists
  const safeData = data || {
    location: 'Paradise Island',
    vibe: 'Luxe Travel',
    gender: 'Feminine',
    skinTone: 'Deep Ebony',
    hairType: '4C Coils',
    mobility: 'None',
    experience: 'Arrival Scene',
    pose: 'Relaxed and radiant',
    relationship: 'Solo',
    children: 'No Children',
    cameraAngle: 'Eye Level Shot',
    outputType: 'static'
  };

  const safePrompt = prompt || "Experience inclusive travel through the lens of a traveler, captured confidently at a stunning destination.";

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-yellow-400">Professional Media Kit</h2>
          <button
            onClick={downloadMediaKit}
            className="bg-blue-500 hover:bg-blue-400 text-white px-6 py-3 rounded-lg flex items-center space-x-2"
          >
            <SafeIcon icon={FiDownload} />
            <span>Download PDF</span>
          </button>
        </div>

        {/* Media Kit Preview */}
        <div className="bg-white text-black rounded-lg p-8 mb-6">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Travel Content Creator</h1>
            <p className="text-gray-600 text-lg">Authentic Inclusive Travel Content & Brand Partnerships</p>
            <div className="flex items-center justify-center space-x-4 mt-4 text-gray-500">
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiMapPin} />
                <span>{safeData.location}</span>
              </div>
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiCamera} />
                <span>{safeData.outputType === 'video' ? 'Video Content' : 'Photography'}</span>
              </div>
            </div>
          </div>

          {/* Content Showcase based on Generated Prompt */}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold mb-4 text-gray-900">Featured Content</h3>
            <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg p-6 text-white mb-4">
              <div className="text-center">
                <h4 className="text-xl font-bold mb-2">{safeData.experience}</h4>
                <p className="text-lg mb-2">{safeData.vibe} • {safeData.location}</p>
                <p className="text-sm opacity-90">
                  {safeData.gender} traveler with {safeData.skinTone?.toLowerCase()} skin 
                  {safeData.hairType ? ` and ${safeData.hairType?.toLowerCase()} hair` : ''}
                </p>
              </div>
            </div>
            <div className="bg-gray-100 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Content Description</h4>
              <p className="text-gray-700 leading-relaxed">{safePrompt}</p>
            </div>
          </div>

          {/* Audience Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-900">Audience Demographics</h3>
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900">Primary Audience</h4>
                  <p className="text-gray-700">
                    {safeData.gender} travelers, ages 25-45, interested in 
                    {safeData.vibe?.toLowerCase()} experiences
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900">Content Focus</h4>
                  <p className="text-gray-700">
                    Inclusive representation in {safeData.location} with 
                    emphasis on authentic {safeData.vibe?.toLowerCase()} experiences
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900">Accessibility Commitment</h4>
                  <p className="text-gray-700">
                    {safeData.mobility !== 'None' 
                      ? `Featuring ${safeData.mobility} accessibility and inclusive travel options`
                      : 'Universal accessibility awareness and inclusive travel advocacy'
                    }
                  </p>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-900">Performance Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <SafeIcon icon={FiUsers} className="text-2xl text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-blue-600">{metrics.followers}</div>
                  <div className="text-sm text-gray-600">Followers</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <SafeIcon icon={FiTrendingUp} className="text-2xl text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-600">{metrics.engagement}</div>
                  <div className="text-sm text-gray-600">Engagement</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <SafeIcon icon={FiBarChart3} className="text-2xl text-purple-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-purple-600">{metrics.reach}</div>
                  <div className="text-sm text-gray-600">Monthly Reach</div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <SafeIcon icon={FiInstagram} className="text-2xl text-orange-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-orange-600">{metrics.impressions}</div>
                  <div className="text-sm text-gray-600">Impressions</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Specifications */}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold mb-4 text-gray-900">Content Specifications</h3>
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Visual Style</h4>
                  <p className="text-gray-700 text-sm">
                    {safeData.cameraAngle} perspective featuring 
                    {safeData.pose ? ` ${safeData.pose?.toLowerCase()}` : ' authentic'} poses
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Content Type</h4>
                  <p className="text-gray-700 text-sm">
                    {safeData.outputType === 'video' ? 'Video content with cinematic quality' : 'High-resolution photography'}
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Brand Alignment</h4>
                  <p className="text-gray-700 text-sm">
                    Perfect for inclusive brands targeting diverse travelers and {safeData.vibe?.toLowerCase()} experiences
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sample Social Media Caption */}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold mb-4 text-gray-900">Sample Social Content</h3>
            <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-yellow-400">
              <p className="text-gray-700 italic mb-2">
                "✨ Living my best {safeData.vibe?.toLowerCase()} life at {safeData.location}! 
                As a {safeData.gender?.toLowerCase()} traveler
                {safeData.skinTone ? ` with ${safeData.skinTone?.toLowerCase()} skin` : ''}, 
                I'm here to show you that every destination is meant for everyone. 🌍
              </p>
              <p className="text-gray-700 italic">
                This {safeData.experience?.toLowerCase()} reminded me why representation matters in travel. 
                {safeData.hairType ? ` Your ${safeData.hairType?.toLowerCase()} hair and unique beauty deserve to be celebrated!` : ' Your authentic self deserves to be celebrated!'}
                #Travel #{safeData.location?.replace(/\s+/g, '')}Travel"
              </p>
            </div>
          </div>

          {/* Partnership Opportunities */}
          <div className="mb-8">
            <h3 className="text-2xl font-semibold mb-4 text-gray-900">Brand Partnership Opportunities</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                  <SafeIcon icon={FiHeart} className="mr-2" />
                  Travel & Hospitality
                </h4>
                <p className="text-blue-800 text-sm">
                  Showcase accommodations, destinations, and experiences through authentic perspective
                </p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                  <SafeIcon icon={FiCamera} className="mr-2" />
                  Beauty & Lifestyle
                </h4>
                <p className="text-green-800 text-sm">
                  Feature products that complement diverse styles and aesthetics in travel settings
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center bg-gradient-to-r from-yellow-400 to-orange-400 p-6 rounded-lg">
            <h3 className="text-2xl font-bold text-black mb-2">Ready to Collaborate?</h3>
            <p className="text-gray-800 mb-4">
              Let's create authentic content that showcases your brand through the lens of inclusive travel
            </p>
            <p className="text-black font-semibold">
              📧 partnerships@travelcreator.com | 📱 @travelcreator
            </p>
          </div>
        </div>

        {/* Deliverables */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-700 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Content Deliverables</h3>
            <ul className="space-y-2 text-gray-300">
              <li>• {safeData.outputType === 'video' ? '6 TikTok/Reel videos' : '12 high-res photos'}</li>
              <li>• 3 Instagram carousel posts</li>
              <li>• 5 caption variations per post</li>
              <li>• Stories content (10+ frames)</li>
              <li>• Blog post feature (800+ words)</li>
              <li>• Usage rights and raw content</li>
            </ul>
          </div>
          <div className="bg-gray-700 rounded-lg p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Platform Distribution</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiInstagram} className="text-pink-400" />
                  <span className="text-gray-300">Instagram</span>
                </div>
                <span className="text-white font-semibold">89.2K</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiTwitter} className="text-blue-400" />
                  <span className="text-gray-300">TikTok</span>
                </div>
                <span className="text-white font-semibold">36.1K</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiUsers} className="text-green-400" />
                  <span className="text-gray-300">YouTube</span>
                </div>
                <span className="text-white font-semibold">12.5K</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaKit;